import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo,useRef, useState } from 'react';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Divider,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem, FormScan } from '@ekhodealer/ekho-common';

import Upload16 from '@common/components/icons/Upload16';
import { InfoLineItem } from '@common/components/InfoLineItem';

import { useGetShipSellerFormsToEkho } from '../../../api/progress/useGetShipSellerFormsToEkho';
import { useShipSellerFormsToEkho } from '../../../api/progress/useShipSellerFormsToEkho';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

import { downloadPdf } from './utils/downloadPdf';
import { fileToBase64 } from './utils/fileToBase64';

interface ShipSellerFormsToEkhoDetailProps {
	actionItem: ActionItem;
}

/**
 * Represents a form that needs to be scanned and uploaded
 */
interface FormUploadState {
	formKey: string;
	formName: string;
	file: File | null;
	isUploaded: boolean;
	isAlreadyUploaded: boolean;
}

/**
 * ShipSellerFormsToEkhoDetail component for shipping MSO and counter-signed paperwork back to Ekho.
 * Two-section layout:
 * 1. Upload scans of each form (list with upload buttons that turn to checkmarks)
 * 2. Generate shipping label (dealer info + button, only enabled when all scans uploaded)
 */
export const ShipSellerFormsToEkhoDetail = ({ actionItem }: ShipSellerFormsToEkhoDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	// Create refs for file inputs - one per form
	const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

	const shipFormsMutation = useShipSellerFormsToEkho(orderId);
	const { data: dealerData, isLoading: isLoadingDealerData } =
		useGetShipSellerFormsToEkho(orderId);

	// Track uploaded files state
	const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});

	// Build form states from dealer data
	const formStates: FormUploadState[] = useMemo(() => {
		if (!dealerData?.documentsToShip) return [];

		return dealerData.documentsToShip.map((formName) => {
			// Use form name as key (sanitized)
			const formKey = formName;
			const isAlreadyUploaded = dealerData.uploadedScans?.[formKey] === true;
			const hasLocalFile = !!uploadedFiles[formKey];

			return {
				formKey,
				formName,
				file: uploadedFiles[formKey] || null,
				isUploaded: isAlreadyUploaded || hasLocalFile,
				isAlreadyUploaded,
			};
		});
	}, [dealerData, uploadedFiles]);

	// Check if all forms have been uploaded
	const allFormsUploaded = useMemo(() => {
		if (formStates.length === 0) return false;
		return formStates.every((form) => form.isUploaded);
	}, [formStates]);

	const handleFileSelect = (formKey: string, file: File) => {
		setUploadedFiles((prev) => ({
			...prev,
			[formKey]: file,
		}));
	};

	const handleReplaceFile = (formKey: string) => {
		// Remove the file from local state
		setUploadedFiles((prev) => {
			const newFiles = { ...prev };
			delete newFiles[formKey];
			return newFiles;
		});
	};

	const handleSubmit = async () => {
		try {
			// Convert all uploaded files to FormScan format
			const formScans: FormScan[] = await Promise.all(
				Object.entries(uploadedFiles).map(async ([formKey, file]) => {
					const base64Data = await fileToBase64(file);
					return {
						formKey,
						file: {
							base64Data,
							type: file.type || 'application/pdf',
							name: file.name,
						},
					};
				}),
			);

			const result = await shipFormsMutation.mutateAsync({ formScans });

			// Download the shipping label PDF
			if (result.shippingLabelPdf) {
				const filename = `dealer-return-packet-shipping-label-${orderId}.pdf`;
				downloadPdf(result.shippingLabelPdf, filename);
			}

			toast({
				message: `Return shipping label generated successfully. Tracking: ${result.trackingNumber}`,
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to generate return shipping label';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	};

	const isFormDisabled =
		shipFormsMutation.isPending || actionItem.status === ActionItemStatus.COMPLETE;

	const formatAddress = (address: {
		street: string;
		city: string;
		state: string;
		zipCode: string;
	}) => {
		return `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
	};

	// If already complete, show completed state
	if (actionItem.status === ActionItemStatus.COMPLETE) {
		return (
			<Stack itemsSpacing='24'>
				<Stack itemsSpacing='8'>
					<Text size='body' weight='semibold'>
						{actionItem.config.title}
					</Text>
					<Text size='bodySmall' color='success'>
						✓ Forms have been scanned and shipped to Ekho
					</Text>
				</Stack>
				{dealerData?.trackingNumber && (
					<InfoLineItem
						title='Tracking number'
						value={dealerData.trackingNumber}
						isCopyable
					/>
				)}
			</Stack>
		);
	}

	return (
		<Stack itemsSpacing='24'>
			{/* Header */}
			<Stack itemsSpacing='8'>
				<Text size='bodySmall' color='secondary'>
					Scan and upload each form listed below, then generate a shipping label to send
					the original documents back to Ekho.
				</Text>
			</Stack>

			{isLoadingDealerData ? (
				<Stack itemsSpacing='8'>
					<SkeletonRect width='60%' height={20} />
					<SkeletonRect width='80%' height={20} />
					<SkeletonRect width='70%' height={20} />
				</Stack>
			) : (
				<>
					{/* Section 1: Upload scans of forms */}
					<Stack itemsSpacing='16'>
						<Stack itemsSpacing='4'>
							<Text size='body' weight='semibold'>
								1. Upload scans of the following forms
							</Text>
							<Text size='footnote' color='secondary'>
								All forms must be uploaded before you can generate a shipping label.
							</Text>
						</Stack>

						<Stack itemsSpacing='4'>
							{formStates.map((form) => (
								<div key={form.formKey}>
									<input
										ref={(el) => {
											fileInputRefs.current[form.formKey] = el;
										}}
										type='file'
										accept='.pdf,.png,.jpg,.jpeg'
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) {
												handleFileSelect(form.formKey, file);
											}
										}}
										style={{ display: 'none' }}
										disabled={isFormDisabled}
									/>
									<Group
										key={form.formKey}
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='space-between'>
										<Stack itemsSpacing='2'>
											<Text size='bodySmall'>{form.formName}</Text>
											{form.file && (
												<Text size='footnote' color='secondary'>
													{form.file.name}
												</Text>
											)}
										</Stack>

										{form.isUploaded ? (
											form.isAlreadyUploaded ? (
												<Text size='bodySmall' color='success'>
													✓ Uploaded
												</Text>
											) : (
												<Group itemsSpacing='8' itemsAlignY='center'>
													<Text size='bodySmall' color='secondary'>
														Uploaded
													</Text>
													<Button
														variant='ghost'
														size='small'
														onPress={() => {
															handleReplaceFile(form.formKey);
														}}
														isDisabled={isFormDisabled}
														aria-label={`Replace ${form.formName}`}>
														Replace
													</Button>
												</Group>
											)
										) : (
											<Button
												variant='secondary'
												size='medium'
												onPress={() => {
													fileInputRefs.current[form.formKey]?.click();
												}}
												isDisabled={isFormDisabled}
												aria-label={`Upload ${form.formName}`}>
												<Group itemsSpacing='4' itemsAlignY='center'>
													<Upload16 />
													<Text size='bodySmall'>Upload</Text>
												</Group>
											</Button>
										)}
									</Group>
								</div>
							))}
						</Stack>
					</Stack>

					<Divider />

					{/* Section 2: Generate shipping label */}
					<Stack itemsSpacing='16'>
						<Text size='body' weight='semibold'>
							2. Generate shipping label to ship forms back to us
						</Text>

						{dealerData && (
							<Stack itemsSpacing='12'>
								<InfoLineItem title='Ship to' value={dealerData.dealerName} />
								<InfoLineItem
									title='Destination address'
									value={formatAddress(dealerData.dealerAddress)}
								/>

								{dealerData.hasBeenShipped && dealerData.trackingNumber && (
									<>
										<InfoLineItem title='Status' value='Already shipped' />
										<InfoLineItem
											title='Tracking number'
											value={dealerData.trackingNumber}
											isCopyable
										/>
									</>
								)}
							</Stack>
						)}

						<Group itemsSpacing='8' noWrap>
							<Group.Item grow={1}>
								<Button
									variant='secondary'
									size='large'
									isFullWidth
									onPress={navigateBack}
									isDisabled={shipFormsMutation.isPending}>
									Cancel
								</Button>
							</Group.Item>
							<Group.Item grow={1}>
								<Button
									size='large'
									isFullWidth
									onPress={handleSubmit}
									isLoading={shipFormsMutation.isPending}
									isDisabled={
										!allFormsUploaded || isLoadingDealerData || isFormDisabled
									}>
									{'Generate Return Shipping Label'}
								</Button>
							</Group.Item>
						</Group>
					</Stack>
				</>
			)}
		</Stack>
	);
};
